<script>
  import { onDestroy, onMount } from 'svelte';
  import { ArrowDown, ArrowUp, CircleGauge, Network, Route, Wifi } from 'lucide-svelte';

  export let session = null;
  export let callStatus = 'connecting';

  let timer;
  let previous = { timestamp: 0, received: 0, sent: 0 };
  let diagnostics = {
    receiveKbps: 0,
    sendKbps: 0,
    quality: 'Đang đo',
    qualityTone: 'pending',
    networkLabel: 'Không xác định',
    networkDetail: 'Trình duyệt chưa cung cấp thông tin',
    route: 'unknown',
    protocol: '',
    rtt: null,
    packetLoss: null,
    routeAttempts: createRouteAttemptState()
  };

  const routeSteps = [
    { id: 'host', title: 'Host', detail: 'Kết nối nội bộ / trực tiếp' },
    { id: 'srflx', title: 'Reflexive', detail: 'Địa chỉ công khai qua STUN' },
    { id: 'relay', title: 'Relay', detail: 'Chuyển tiếp an toàn qua TURN' }
  ];
  let routeAttemptHistory = createRouteAttemptState();

  onMount(() => {
    updateDiagnostics();
    timer = setInterval(updateDiagnostics, 2000);
  });

  onDestroy(() => clearInterval(timer));

  async function updateDiagnostics() {
    const network = readNetworkType();
    let report = null;
    try {
      report = await session?.getStats?.();
    } catch {
      report = null;
    }
    if (!report) {
      diagnostics = { ...diagnostics, ...network, quality: statusQuality(callStatus), qualityTone: statusTone(callStatus) };
      return;
    }

    let received = 0;
    let sent = 0;
    let lost = 0;
    let packets = 0;
    let selectedPair = null;
    let transport = null;
    const byId = new Map();

    report.forEach((stat) => {
      byId.set(stat.id, stat);
      if (stat.type === 'inbound-rtp' && !stat.isRemote) {
        received += Number(stat.bytesReceived || 0);
        lost += Number(stat.packetsLost || 0);
        packets += Number(stat.packetsReceived || 0);
      }
      if (stat.type === 'outbound-rtp' && !stat.isRemote) sent += Number(stat.bytesSent || 0);
      if (stat.type === 'transport' && stat.selectedCandidatePairId) transport = stat;
      if (isCandidatePair(stat) && isUsablePair(stat)) selectedPair = stat;
    });

    if (transport?.selectedCandidatePairId) selectedPair = byId.get(transport.selectedCandidatePairId) || selectedPair;
    const localCandidate = selectedPair ? byId.get(selectedPair.localCandidateId) : null;
    const remoteCandidate = selectedPair ? byId.get(selectedPair.remoteCandidateId) : null;
    const selectedRoute = pairRoute(localCandidate, remoteCandidate, selectedPair);
    routeAttemptHistory = mergeRouteAttempts(routeAttemptHistory, readRouteAttempts(report, byId, selectedPair));
    const timestamp = Date.now();
    const seconds = previous.timestamp ? Math.max((timestamp - previous.timestamp) / 1000, 0.5) : 0;
    const receiveKbps = seconds ? Math.max(0, ((received - previous.received) * 8) / seconds / 1000) : 0;
    const sendKbps = seconds ? Math.max(0, ((sent - previous.sent) * 8) / seconds / 1000) : 0;
    const packetLoss = packets + lost > 0 ? (lost / (packets + lost)) * 100 : null;
    const rtt = selectedPair?.currentRoundTripTime != null ? selectedPair.currentRoundTripTime * 1000 : null;
    const quality = calculateQuality(callStatus, rtt, packetLoss);

    previous = { timestamp, received, sent };
    diagnostics = {
      receiveKbps,
      sendKbps,
      quality: quality.label,
      qualityTone: quality.tone,
      ...network,
      route: selectedRoute,
      protocol: [localCandidate?.protocol, selectedPair?.transport || remoteCandidate?.protocol].filter(Boolean).join(' / ').toUpperCase(),
      rtt,
      packetLoss,
      routeAttempts: routeAttemptHistory
    };
  }

  function createRouteAttemptState() {
    return {
      host: createRouteAttempt(),
      srflx: createRouteAttempt(),
      relay: createRouteAttempt()
    };
  }

  function createRouteAttempt() {
    return {
      gathered: false,
      checking: false,
      succeeded: false,
      failed: false,
      selected: false,
      lastState: 'pending'
    };
  }

  function readRouteAttempts(report, byId, selectedPair) {
    const attempts = createRouteAttemptState();

    report.forEach((stat) => {
      if (isCandidateStat(stat)) {
        const route = normalizeRoute(stat.candidateType);
        if (attempts[route]) {
          attempts[route].gathered = true;
          attempts[route].lastState = 'gathered';
        }
      }
    });

    report.forEach((stat) => {
      if (!isCandidatePair(stat)) return;

      const local = byId.get(stat.localCandidateId);
      const remote = byId.get(stat.remoteCandidateId);
      const route = pairRoute(local, remote, stat);
      if (!attempts[route]) return;

      attempts[route].gathered = attempts[route].gathered || Boolean(local || remote);
      if (stat.state === 'succeeded') {
        attempts[route].succeeded = true;
        attempts[route].lastState = 'succeeded';
      } else if (stat.state === 'failed') {
        attempts[route].failed = true;
        attempts[route].lastState = 'failed';
      } else if (['in-progress', 'checking', 'waiting'].includes(stat.state)) {
        attempts[route].checking = true;
        attempts[route].lastState = 'checking';
      }

      if (selectedPair && stat.id === selectedPair.id) {
        attempts[route].selected = true;
        attempts[route].succeeded = true;
        attempts[route].lastState = 'selected';
      }
    });

    return attempts;
  }

  function mergeRouteAttempts(previousAttempts, nextAttempts) {
    const merged = createRouteAttemptState();
    for (const route of Object.keys(merged)) {
      const previousRoute = previousAttempts?.[route] || createRouteAttempt();
      const nextRoute = nextAttempts?.[route] || createRouteAttempt();
      merged[route] = {
        gathered: previousRoute.gathered || nextRoute.gathered,
        checking: nextRoute.checking,
        succeeded: previousRoute.succeeded || nextRoute.succeeded,
        failed: previousRoute.failed || nextRoute.failed,
        selected: nextRoute.selected,
        lastState: nextRoute.selected
          ? 'selected'
          : nextRoute.checking
            ? 'checking'
            : nextRoute.failed
              ? 'failed'
              : nextRoute.succeeded
                ? 'succeeded'
                : previousRoute.lastState
      };
    }
    return merged;
  }

  function isCandidatePair(stat) {
    return stat?.type === 'candidate-pair' || stat?.type === 'candidatepair';
  }

  function isCandidateStat(stat) {
    return ['local-candidate', 'remote-candidate', 'localcandidate', 'remotecandidate'].includes(stat?.type);
  }

  function isUsablePair(stat) {
    return stat?.selected || (stat?.state === 'succeeded' && (stat?.nominated || stat?.writable || stat?.requestsReceived > 0 || stat?.responsesReceived > 0));
  }

  function normalizeRoute(candidateType) {
    const type = String(candidateType || '').toLowerCase();
    if (type === 'prflx' || type === 'peerreflexive') return 'srflx';
    if (type === 'serverreflexive') return 'srflx';
    return ['host', 'srflx', 'relay'].includes(type) ? type : 'unknown';
  }

  function pairRoute(local, remote, pair = null) {
    const routes = [
      local?.candidateType,
      local?.type,
      remote?.candidateType,
      remote?.type,
      pair?.localCandidateType,
      pair?.remoteCandidateType
    ].map(normalizeRoute);
    if (routes.includes('relay')) return 'relay';
    if (routes.includes('srflx')) return 'srflx';
    return routes.includes('host') ? 'host' : 'unknown';
  }

  function readNetworkType() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const type = connection?.type;
    const effectiveType = connection?.effectiveType;
    const labels = {
      wifi: 'Wi-Fi',
      cellular: 'Mạng di động',
      ethernet: 'Ethernet',
      bluetooth: 'Bluetooth',
      wimax: 'WiMAX',
      other: 'Mạng khác',
      unknown: 'Không xác định'
    };

    let networkLabel = labels[type] || 'Không xác định';
    if (!type && navigator.onLine) {
      networkLabel = 'Mạng đang hoạt động';
    }

    const qualityLabel = effectiveType
      ? `Chất lượng ước tính ${String(effectiveType).toUpperCase()}`
      : null;

    return {
      networkLabel,
      networkDetail: connection?.downlink
        ? `${qualityLabel ? `${qualityLabel} · ` : ''}Ước tính ${connection.downlink} Mbps${connection.rtt ? ` · RTT ${connection.rtt} ms` : ''}`
        : qualityLabel || 'Trình duyệt không công khai chính xác loại Wi-Fi/5G'
    };
  }

  function calculateQuality(state, rtt, loss) {
    if (state !== 'connected') return { label: statusQuality(state), tone: statusTone(state) };
    if ((loss ?? 0) >= 8 || (rtt ?? 0) >= 500) return { label: 'Kém', tone: 'bad' };
    if ((loss ?? 0) >= 3 || (rtt ?? 0) >= 250) return { label: 'Trung bình', tone: 'warning' };
    return { label: 'Ổn định', tone: 'good' };
  }

  function statusQuality(state) {
    return state === 'reconnecting' || state === 'disconnected' ? 'Đang khôi phục' : state === 'failed' ? 'Mất kết nối' : 'Đang đo';
  }

  function statusTone(state) {
    return state === 'failed' ? 'bad' : state === 'reconnecting' || state === 'disconnected' ? 'warning' : 'pending';
  }

  function formatRate(value) {
    if (value >= 1000) return `${(value / 1000).toFixed(1)} Mbps`;
    return `${Math.round(value)} Kbps`;
  }

  function routeStateLabel(routeId) {
    const attempt = diagnostics.routeAttempts?.[routeId];
    if (!attempt?.gathered) return 'Chưa thấy';
    if (attempt.selected) return 'Đang dùng';
    if (attempt.checking) return 'Đang thử';
    if (attempt.failed && !attempt.succeeded) return 'Không đi được';
    if (attempt.failed && attempt.succeeded) return 'Đã thử lại';
    if (attempt.succeeded) return 'Dùng được';
    return 'Đã thấy';
  }

  function routeStateTone(routeId) {
    const attempt = diagnostics.routeAttempts?.[routeId];
    if (!attempt?.gathered) return 'pending';
    if (attempt.selected) return 'active';
    if (attempt.checking) return 'checking';
    if (attempt.failed && !attempt.succeeded) return 'failed';
    if (attempt.succeeded) return 'ok';
    return 'seen';
  }

  function routeHelpText() {
    const attempts = diagnostics.routeAttempts || {};
    if (diagnostics.route === 'relay' && attempts.srflx?.failed) {
      return 'STUN đã tạo được srflx nhưng kiểm tra kết nối không đi qua, nên cuộc gọi đang dùng TURN relay.';
    }
    if (diagnostics.route === 'relay') {
      return 'Cuộc gọi đang đi qua TURN để đảm bảo kết nối ổn định.';
    }
    if (diagnostics.route === 'srflx') {
      return 'Cuộc gọi đang kết nối trực tiếp qua địa chỉ STUN.';
    }
    if (diagnostics.route === 'host') {
      return 'Cuộc gọi đang dùng đường kết nối nội bộ trực tiếp.';
    }
    return 'Đường kết nối sẽ hiển thị khi WebRTC chọn xong tuyến truyền.';
  }
</script>

<section class="network-diagnostics">
  <div class="metric-grid">
    <article>
      <span class="metric-icon receive"><ArrowDown size={15} /></span>
      <div><small>Nhận</small><strong>{formatRate(diagnostics.receiveKbps)}</strong></div>
    </article>
    <article>
      <span class="metric-icon send"><ArrowUp size={15} /></span>
      <div><small>Gửi</small><strong>{formatRate(diagnostics.sendKbps)}</strong></div>
    </article>
    <article>
      <span class="metric-icon"><CircleGauge size={15} /></span>
      <div><small>Trạng thái</small><strong class={diagnostics.qualityTone}>{diagnostics.quality}</strong></div>
    </article>
  </div>

  <div class="info-card">
    <div class="info-icon"><Wifi size={17} /></div>
    <div>
      <small>Loại mạng đang dùng</small>
      <strong>{diagnostics.networkLabel}</strong>
      <p>{diagnostics.networkDetail}</p>
    </div>
  </div>

  <div class="route-card">
    <div class="route-title">
      <div><Route size={16} /><strong>Đường kết nối WebRTC</strong></div>
      {#if diagnostics.protocol}<span>{diagnostics.protocol}</span>{/if}
    </div>
    <div class="route-flow">
      {#each routeSteps as step, index}
        <div
          class:active={diagnostics.route === step.id}
          class:passed={routeSteps.findIndex(({ id }) => id === diagnostics.route) >= index}
          class="route-step {routeStateTone(step.id)}">
          <span>{index + 1}</span>
          <div><strong>{step.title}</strong><small>{step.detail}</small></div>
          <em>{routeStateLabel(step.id)}</em>
        </div>
      {/each}
    </div>
    <p class="route-help">{routeHelpText()}</p>
    {#if diagnostics.rtt != null || diagnostics.packetLoss != null}
      <div class="network-foot">
        <span>Độ trễ {diagnostics.rtt != null ? `${Math.round(diagnostics.rtt)} ms` : '—'}</span>
        <span>Mất gói {diagnostics.packetLoss != null ? `${diagnostics.packetLoss.toFixed(1)}%` : '—'}</span>
      </div>
    {/if}
  </div>
</section>

<style>
  .network-diagnostics { display:grid; gap:.75rem; }
  .metric-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:.45rem; }
  .metric-grid article { display:flex; align-items:center; gap:.45rem; min-width:0; padding:.6rem; border:1px solid rgba(255,255,255,.09); border-radius:.8rem; background:rgba(255,255,255,.045); }
  .metric-icon { display:grid; place-items:center; flex:0 0 auto; width:1.75rem; height:1.75rem; border-radius:.55rem; color:#cbd5e1; background:rgba(148,163,184,.16); }
  .metric-icon.receive { color:#86efac; background:rgba(34,197,94,.13); }
  .metric-icon.send { color:#93c5fd; background:rgba(59,130,246,.15); }
  small { display:block; color:#94a3b8; font-size:.55rem; line-height:1.35; }
  strong { display:block; margin-top:.12rem; color:#f8fafc; font-size:.65rem; line-height:1.35; }
  strong.good { color:#86efac; } strong.warning { color:#fcd34d; } strong.bad { color:#fca5a5; } strong.pending { color:#cbd5e1; }
  .info-card { display:flex; gap:.65rem; padding:.7rem; border:1px solid rgba(255,255,255,.09); border-radius:.9rem; background:rgba(255,255,255,.045); }
  .info-icon { display:grid; place-items:center; flex:0 0 auto; width:2rem; height:2rem; border-radius:.65rem; color:#93c5fd; background:rgba(59,130,246,.15); }
  .info-card p,.route-help { margin-top:.2rem; color:#94a3b8; font-size:.56rem; line-height:1.5; }
  .route-card { padding:.75rem; border:1px solid rgba(255,255,255,.09); border-radius:.9rem; background:rgba(2,6,23,.25); }
  .route-title,.route-title div,.network-foot { display:flex; align-items:center; justify-content:space-between; gap:.4rem; }
  .route-title div { justify-content:flex-start; color:#93c5fd; }
  .route-title span { padding:.2rem .38rem; border-radius:999px; color:#bfdbfe; background:rgba(59,130,246,.15); font-size:.5rem; font-weight:700; }
  .route-flow { display:grid; gap:.35rem; margin-top:.65rem; }
  .route-step { display:flex; align-items:center; gap:.5rem; min-height:2.55rem; padding:.42rem; border-radius:.65rem; opacity:.48; }
  .route-step>span { display:grid; place-items:center; width:1.35rem; height:1.35rem; border:1px solid rgba(148,163,184,.3); border-radius:999px; color:#94a3b8; font-size:.55rem; font-weight:700; }
  .route-step.passed { opacity:.76; }
  .route-step.active { opacity:1; background:rgba(59,130,246,.12); }
  .route-step.active>span { border-color:#60a5fa; color:white; background:#2563eb; }
  .route-step.failed { opacity:.9; background:rgba(239,68,68,.08); }
  .route-step.checking { opacity:.95; background:rgba(245,158,11,.08); }
  .route-step.ok,.route-step.seen { opacity:.82; }
  .route-step strong { margin:0; font-size:.6rem; }
  .route-step small { font-size:.5rem; }
  .route-step em { margin-left:auto; flex:0 0 auto; min-width:4.8rem; padding:.18rem .35rem; border-radius:999px; color:#cbd5e1; background:rgba(148,163,184,.13); font-size:.48rem; font-style:normal; font-weight:750; text-align:center; }
  .route-step.active em { color:#bfdbfe; background:rgba(59,130,246,.22); }
  .route-step.failed em { color:#fecaca; background:rgba(239,68,68,.18); }
  .route-step.checking em { color:#fde68a; background:rgba(245,158,11,.18); }
  .network-foot { margin-top:.55rem; padding-top:.55rem; border-top:1px solid rgba(255,255,255,.08); color:#cbd5e1; font-size:.53rem; }
  @media(max-width:520px){ .metric-grid{grid-template-columns:1fr}.metric-grid article{padding:.5rem} }
</style>
